import { Stack } from '@carbon/react';
import _ from 'lodash';

import QuickViewBox from 'shared/components/quick-view/QuickViewBox';
import { formatArea, formatNumeric } from 'utils/format';
import styles from 'theme/components/outputs/outputs-overview.module.scss';

const OverviewRow = ({ children }) => (
  <Stack gap={5} orientation="horizontal" className={styles['overview-row']}>
    {children}
  </Stack>
);

const OutputsOverview = ({ overview, project, isCompact = false }) => {
  const renderOverview = (name, value, tooltip) => (
    <QuickViewBox key={name} name={name} value={value} isLoading={!overview} tooltip={tooltip} />
  );

  const renderOverviewBox = ({ title, overviews, fullWidthOverviews = [] }) => (
    <Stack gap={2}>
      <h5>
        <strong>{title}</strong>
      </h5>
      <Stack gap={4}>
        {_.chunk(overviews, isCompact ? 3 : 2).map((group, index) => (
          <OverviewRow key={index}>{group}</OverviewRow>
        ))}
        {fullWidthOverviews.map((group, index) => (
          <OverviewRow key={index}>{group}</OverviewRow>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Stack gap={6}>
      {renderOverviewBox({
        title: 'Headcount',
        overviews: [
          renderOverview(
            'Assigned Headcount',
            formatNumeric(overview?.assignedHeadcount),
            'Number of seated and mobile population. It does not include people who work from home full-time or work at client sites.'
          ),
          renderOverview(
            'Attending Population',
            formatNumeric(overview?.attendingPopulation),
            'Total number of on-site population per the Attendance Rates provided or specified as defaults in the Inputs page.'
          ),
          renderOverview(
            'Attendance Rate',
            `${overview?.avgAttendanceRate}%`,
            'The average amount of time Business Units expect their population to be on site. A 40% Average Attendance Rate, for example, would indicate that BUs, on average, expect their population to be in the office 2 out of 5 days per week.'
          )
        ]
      })}
      {renderOverviewBox({
        title: 'Workplace Capacity',
        overviews: [
          renderOverview(
            'Traditional Workstations',
            formatNumeric(overview?.traditionalWorkpoints),
            'Total number of required traditional workstations.'
          ),
          renderOverview('% Workstations / Attendee', `${overview?.workstationAtt}%`),
          renderOverview(
            'Total Weighted Capacity',
            formatNumeric(overview?.totalWeightedCapacity),
            'Total adjusted value of seats, depending on their ergonomic support over time and location.'
          ),
          renderOverview('% Weighted Capacity / Attendee', `${overview?.weightedCapacityAtt}%`),
          renderOverview(
            'Total Capacity',
            formatNumeric(overview?.totalCapacity),
            'Overall number of seats at a site where work can be done.'
          ),
          renderOverview('% Capacity / Attendee', `${overview?.capacityAtt}%`)
        ]
      })}
      {renderOverviewBox({
        title: 'Workplace Area',
        overviews: [
          renderOverview(
            'Workplace Subtotal',
            formatArea(overview?.subtotalArea, project?.details.measurementSystem),
            'Combined total area from all Collaboration, Individual, Community, and Support spaces as well as Specialty spaces and Site-Specific spaces. It does not include the circulation amount.'
          ),
          renderOverview(
            'Workplace Circulation',
            formatArea(overview?.circulation, project?.details.measurementSystem),
            'Total circulation area as produced by the percentage noted on the Inputs page.'
          ),
          renderOverview(
            'Total Usable / Plannable Area',
            formatArea(overview?.totalPlannable, project?.details.measurementSystem),
            'The amount of space required to fulfill the workplace requirements (calculated from the LCS), Specialty spaces, Site-Specific spaces, and circulation. This is not Rentable/Lettable area.'
          ),
          renderOverview(
            'Total Rentable / Lettable Area',
            formatArea(overview?.totalRentable, project?.details.measurementSystem),
            'Total Usable/Plannable is combined with a 4% add-on to account for building support (elevator lobbies, restrooms) and a x% loss factor from the inputs page. This is an approximation of the amount of space that would need to be leased to fulfill all space requirements for this project. If this block is showing zero, you have selected an unknown loss factor as your input, and rentable area cannot be calculated.'
          ),
          renderOverview(
            'Available Area (If Known)',
            formatArea(overview?.availableArea, project?.details.measurementSystem),
            'Available Usable/Plannable area for an existing project or a newly allocated site as provided in the Inputs page.'
          )
        ],
        fullWidthOverviews: [
          renderOverview(
            'Workplace Area/Attending Population',
            formatArea(overview?.totalAreaAttPopulation, project?.details.measurementSystem),
            <>
              Net Usable/Plannable Workplace area per Attending Population (not total allocated headcount). This value
              does not take into account Specialty spaces. Target range should be 97-129 SF or 9-12 SQM per Attending
              Population. <br /> <br />
              <strong>Target Range:</strong> <br />
              Tier 1: 120 - 130 SF or 11 - 12 SQM per attending population <br />
              Tier 2: 100 - 110 SF or 9 - 10 SQM per attending population <br />
              Tier 3: 110 - 120 SF or 10 - 11 SQM per attending population <br />
            </>
          ),
          renderOverview(
            'Sharing Ratio (Att. Population:Weighted Capacity)',
            overview ? `1:${overview.sharingCapacity}` : ''
          )
        ]
      })}
    </Stack>
  );
};

export default OutputsOverview;
